import React, { useState } from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { fetchTalkSession } from '../../util/api';
import { cancelModalCopyId, cancelNoticeLabel } from '../../util/cancelPolicy';
import { isTalkerPendingCancelUnavailableError } from '../../util/editUpcomingBooking';

import Modal from '../Modal/Modal';
import DeclineSuggestTimes from '../DeclineSuggestTimes/DeclineSuggestTimes';
import { declineWithSuggestions } from '../../util/declineSuggestions';

import css from './CancelBookingModal.module.css';

const sessionPayload = payload => payload?.data?.data || payload?.data || payload || {};

// A fulfilled thunk carries the SDK response (payload.status 200), so only
// an error status (>= 400) on a non-fulfilled result counts as a failure.
// Treating payload.status 200 as rejected showed "We could not cancel" after
// a successful cancel.
const isRejectedThunk = result => {
  if (!result) {
    return false;
  }
  if (result.meta?.requestStatus === 'fulfilled') {
    return false;
  }
  if (result.error || result.meta?.requestStatus === 'rejected') {
    return true;
  }
  const status = Number(result.payload?.status);
  return Number.isFinite(status) && status >= 400;
};

/**
 * Confirm cancel of an upcoming talk session. Talker copy: hold / charge
 * policy, including a 1-hour late-cancel warning. Listener copy: the
 * talker is notified. Only Yes runs cancel.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {boolean} [props.isCustomer]
 * @param {string} [props.transactionId]
 * @param {boolean} [props.inProgress]
 * @param {boolean} [props.legacyPendingCancel]
 * @param {boolean} [props.lateCancel]
 * @param {boolean} [props.startNow]
 * @param {string} [props.listenerName]
 * @param {string} [props.acceptDeadline]
 * @param {boolean} [props.declineSuggest] listener declining a pending request: offer "Suggest another time"
 * @param {string} [props.talkerName]
 * @param {Function} props.onClose
 * @param {Function} props.onConfirm
 * @param {Function} props.onManageDisableScrolling
 * @returns {JSX.Element}
 */
const CancelBookingModal = props => {
  const {
    isOpen,
    isCustomer = true,
    transactionId,
    inProgress = false,
    legacyPendingCancel = false,
    lateCancel = false,
    startNow = false,
    listenerName = '',
    acceptDeadline = '',
    declineSuggest = false,
    talkerName = '',
    onClose,
    onConfirm,
    onManageDisableScrolling,
  } = props;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [suggestPicks, setSuggestPicks] = useState([]);
  const offerSuggest = !isCustomer && !!declineSuggest;

  const name = listenerName || 'the listener';
  const deadline = acceptDeadline || 'the accept deadline';
  const showLegacyNotice = legacyPendingCancel || error === 'legacy_pending';
  const waiting = busy || inProgress;
  const talkerLate = !!isCustomer && !!lateCancel && !startNow;
  const moneyId = cancelModalCopyId({ isCustomer, lateCancel: talkerLate, startNow });
  const cutoff = cancelNoticeLabel();

  const submit = () => {
    if (legacyPendingCancel) {
      setError('legacy_pending');
      return Promise.resolve();
    }
    setBusy(true);
    setError(null);
    const id = typeof transactionId === 'string' ? transactionId : transactionId?.uuid || '';
    const check = id ? fetchTalkSession(id).catch(() => null) : Promise.resolve(null);
    return check
      .then(payload => {
        const session = sessionPayload(payload);
        if (session?.bothJoined === true || session?.clockStarted === true) {
          const bothJoined = new Error('both_joined');
          bothJoined.code = 'both_joined';
          throw bothJoined;
        }
        return offerSuggest ? declineWithSuggestions(id, suggestPicks, onConfirm) : onConfirm();
      })
      .then(result => {
        if (isRejectedThunk(result) || isTalkerPendingCancelUnavailableError(result)) {
          const err = result?.payload || result?.error || result || new Error('cancel_failed');
          if (
            isTalkerPendingCancelUnavailableError(err) ||
            isTalkerPendingCancelUnavailableError(result)
          ) {
            const legacy = new Error('legacy_pending');
            legacy.code = 'legacy_pending';
            throw legacy;
          }
          throw err;
        }
        setBusy(false);
        onClose();
      })
      .catch(err => {
        setBusy(false);
        if (err?.code === 'both_joined') {
          setError('both_joined');
          return;
        }
        if (err?.code === 'legacy_pending' || isTalkerPendingCancelUnavailableError(err)) {
          setError('legacy_pending');
          return;
        }
        setError('failed');
      });
  };

  return (
    <Modal
      id="CancelBookingModal"
      isOpen={!!isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      className={css.layer}
      scrollLayerClassName={css.scrollLayer}
      containerClassName={css.container}
      contentClassName={css.content}
    >
      <div
        className={css.body}
        data-cancel-booking-modal="true"
        data-cancel-role={isCustomer ? 'talker' : 'listener'}
        data-cancel-charge={talkerLate ? 'late' : 'free'}
        role="dialog"
        aria-labelledby="cancel-booking-title"
        aria-describedby="cancel-booking-lede"
      >
        <h2 id="cancel-booking-title" className={css.title}>
          <FormattedMessage
            id={showLegacyNotice ? 'CancelBookingModal.legacyTitle' : 'CancelBookingModal.title'}
          />
        </h2>
        <p
          id="cancel-booking-lede"
          className={talkerLate && !showLegacyNotice ? css.warning : css.lede}
        >
          {showLegacyNotice ? (
            <FormattedMessage id="CancelBookingModal.legacyPending" values={{ name, deadline }} />
          ) : (
            <FormattedMessage id={moneyId} values={{ cutoff }} />
          )}
        </p>
        {offerSuggest && !showLegacyNotice ? (
          <DeclineSuggestTimes
            transactionId={typeof transactionId === 'string' ? transactionId : transactionId?.uuid}
            talkerName={talkerName || undefined}
            onChange={setSuggestPicks}
          />
        ) : null}
        {error && error !== 'legacy_pending' ? (
          <p className={css.error} role="alert">
            <FormattedMessage
              id={error === 'both_joined' ? 'CancelBookingModal.bothJoined' : 'CancelBookingModal.error'}
            />
          </p>
        ) : null}
        <div className={css.actions}>
          {showLegacyNotice ? (
            <button type="button" className={css.keep} onClick={onClose}>
              <FormattedMessage id="CancelBookingModal.legacyClose" />
            </button>
          ) : (
            <>
              <button
                type="button"
                className={css.confirm}
                disabled={waiting}
                onClick={submit}
                data-cancel-booking-confirm="true"
              >
                <FormattedMessage id="CancelBookingModal.confirm" />
              </button>
              <button
                type="button"
                className={css.keep}
                disabled={waiting}
                onClick={onClose}
                data-cancel-booking-keep="true"
              >
                <FormattedMessage id="CancelBookingModal.keep" />
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CancelBookingModal;
